import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface UseSwipeBackOptions {
  /** Edge zone width in px where the swipe can start (default: 30) */
  edgeWidth?: number;
  /** Minimum horizontal distance to trigger navigation (default: 80) */
  threshold?: number;
  /** Whether the hook is active (default: true) */
  enabled?: boolean;
}

/**
 * Detects a swipe-right gesture starting from the left edge of the screen
 * and navigates back in history, similar to native mobile back gestures.
 */
export function useSwipeBack({
  edgeWidth = 30,
  threshold = 80,
  enabled = true,
}: UseSwipeBackOptions = {}) {
  const navigate = useNavigate();
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isEdgeSwipe = useRef(false);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch.clientX <= edgeWidth) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        isEdgeSwipe.current = true;
      } else {
        isEdgeSwipe.current = false;
      }
    },
    [edgeWidth],
  );

  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!isEdgeSwipe.current) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartX.current;
      const dy = Math.abs(touch.clientY - touchStartY.current);

      // Horizontal swipe must be dominant and exceed threshold
      if (dx > threshold && dx > dy * 1.5) {
        navigate(-1);
      }
      isEdgeSwipe.current = false;
    },
    [threshold, navigate],
  );

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, onTouchStart, onTouchEnd]);
}
